import type { ConstruccionDeInforme } from "@/lib/informes";
import type { ConstruccionPropuesta } from "@/lib/rellenoConIa";
import { textoDeDecimal, textoDeNumero } from "@/lib/montos";

import type { FormularioDeConstruccion } from "./construcciones";

/* ---------------------------------------------------------------------------
   "Rellenar con IA" en "Construcciones": traducir lo que propuso el modelo al
   formulario de la pestaña.

   ES EL TERCER GEMELO de `maquinariasConIa.ts` e `instalacionesConIa.ts`, y
   existe por lo mismo que aquellos: esta pestaña tampoco tiene borrador —de
   cada construcción cuelgan fotos, así que cada fila se crea con su propio
   POST— y el riesgo real es el punto de miles. Un "1.250,00" que llegue al
   `<input>` con punto decimal vuelve al servidor valiendo mil coma dos cinco…
   o mil doscientos cincuenta, según cómo lo lea. En un informe pericial, sobre
   una cifra que después se multiplica por la superficie.

   ══ TAMPOCO SE EXTRAJO UN MÓDULO COMÚN, y sigue siendo a conciencia ══════════

   Lo que las tres tienen igual es el PATRÓN (traducir, comparar por nombre,
   resumir), no el código: los campos son otros y el resumen nombra otra cosa.
   Lo que sí se comparte es lo que de verdad se repetía, que es el PANEL
   (`FilasPropuestas`) — ahí viven las decisiones.
--------------------------------------------------------------------------- */

/**
 * Una construcción propuesta, con la forma que tiene el formulario.
 *
 * NO TIENE NINGÚN DEFAULT QUE APLICAR, igual que instalaciones y al revés que
 * maquinaria: el request solo exige `tipo_edificacion`, y ese viene del modelo
 * o la fila se descartó en el servidor. `CONSTRUCCION_VACIA` tiene todos sus
 * campos en blanco por el mismo motivo.
 *
 * `nota_terreno` VA VACÍA SIEMPRE: el servidor no la propone —es lo que anotó
 * quien fue a ver la construcción— así que acá no hay nada que traducir. Se
 * escribe en blanco y no se omite porque el formulario espera el campo.
 *
 * `grupo` y `clase` SON TEXTO y pueden venir en `null`: son agrupadores que una
 * construcción suelta puede no tener. Se traducen a cadena vacía, que es lo que
 * el `<input>` entiende — `null` lo volvería no controlado a mitad de camino.
 */
export function formularioDesdeLaConstruccionPropuesta(
  construccion: ConstruccionPropuesta,
): FormularioDeConstruccion {
  return {
    grupo: construccion.grupo ?? "",
    tipo_edificacion: construccion.tipo_edificacion,
    anio: textoDeNumero(construccion.anio),
    superficie_m2: decimal(construccion.superficie_m2),
    clase: construccion.clase ?? "",
    vida_util_total: textoDeNumero(construccion.vida_util_total),
    vida_util_residual: textoDeNumero(construccion.vida_util_residual),
    valor_reposicion_uf_m2: decimal(construccion.valor_reposicion_uf_m2),
    valor_residual_uf_m2: decimal(construccion.valor_residual_uf_m2),
    ajuste_comercial_pct: decimal(construccion.ajuste_comercial_pct),
    nota_terreno: "",
  };
}

/**
 * ¿Esta construcción ya está en el cuadro?
 *
 * NO SIRVE PARA ESCONDERLA: el panel la lista igual y solo apaga su botón.
 * Filtrarla escondería en silencio algo que el documento sí traía.
 *
 * SE COMPARA EL TIPO Y NADA MÁS, igual que sus dos gemelos. Acá el argumento es
 * el más fuerte de los tres: un predio puede tener dos galpones, pero el
 * documento los nombraría distinto ("Galpón de packing" y "Galpón de
 * maquinaria") porque el cuadro los imprime en filas separadas. La alternativa
 * es peor — sumando el año o la superficie, una cifra mal leída haría pasar por
 * nueva una construcción que ya está, y el duplicado en un cuadro que se
 * valoriza no se ve hasta que el total del predio no cuadra.
 *
 * EL GRUPO NO ENTRA EN LA COMPARACIÓN: es un encabezado de presentación que un
 * documento agrupa y otro no, así que sumarlo haría que la misma casa figurara
 * como nueva por venir bajo "Casas" en vez de suelta.
 */
export function yaEstaEnElCuadroDeConstrucciones(
  tipoEdificacion: string,
  guardadas: ConstruccionDeInforme[],
): boolean {
  const clave = normalizar(tipoEdificacion);

  return guardadas.some(
    (construccion) => normalizar(construccion.tipo_edificacion) === clave,
  );
}

/**
 * Qué decir después del clic.
 *
 * EL VERBO ES "ENCONTRAMOS" Y NO "SE AGREGARON", igual que en maquinaria e
 * instalaciones y al revés que en aguas y suelos: acá todavía no se agregó
 * nada, porque cada construcción se agrega con su propio botón.
 */
export function resumenDeLoPropuestoEnConstrucciones(
  construcciones: number,
  descripcion: boolean,
): string {
  if (construcciones === 0 && !descripcion) {
    // NO SE EXCUSA, igual que instalaciones y al revés que maquinaria: acá el
    // catálogo sí tiene un documento para esta pestaña —el certificado de
    // avalúo del SII publica el tipo, la calidad, el año y la superficie de
    // cada edificación—, así que una propuesta vacía significa que ese papel no
    // está o que el predio no tiene construcciones. Cuál de las dos lo dice la
    // lista de documentos leídos que va al lado.
    return "No encontramos construcciones en los documentos leídos.";
  }

  const partes: string[] = [];

  if (construcciones > 0) {
    partes.push(
      `${construcciones} ${construcciones === 1 ? "construcción" : "construcciones"}`,
    );
  }

  if (descripcion) partes.push("la descripción general");

  return `Encontramos ${partes.join(" y ")}.`;
}

/** Un decimal del modelo, escrito como lo escribiría una persona. */
function decimal(valor: number | null): string {
  return valor === null ? "" : textoDeDecimal(String(valor));
}

function normalizar(tipo: string): string {
  return tipo.trim().toLocaleLowerCase("es").replace(/\s+/g, " ");
}
